import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Span,
  Text,
} from '@/components/atoms'
import { SelectField } from '../select-field'
import { isSameDay, isBeforeDay } from './use-calendar'
import { useCalendarPanel } from './calendar-panel.hook'

export interface CalendarPanelProps {
  viewDate: Date
  days: Date[]
  onPrevMonth: () => void
  onNextMonth: () => void
  selectedDate?: Date
  startDate?: Date
  endDate?: Date
  hoverDate?: Date
  minDate?: Date
  maxDate?: Date
  onDayClick: (date: Date) => void
  onDayHover?: (date: Date | undefined) => void
  onConfirm?: () => void
  onCancel?: () => void
  dropdownMonthYear?: boolean
  onSetViewDate?: (date: Date) => void
}

export function CalendarPanel({
  viewDate,
  days,
  onPrevMonth,
  onNextMonth,
  selectedDate,
  startDate,
  endDate,
  hoverDate,
  minDate,
  maxDate,
  onDayClick,
  onDayHover,
  onConfirm,
  onCancel,
  dropdownMonthYear = false,
  onSetViewDate,
}: CalendarPanelProps) {
  const {
    t,
    monthYear,
    weekdays,
    isPrevDisabled,
    isNextDisabled,
    currentYear,
    monthOptions,
    yearOptions,
    isDropdownMonthYearOpen,
    setIsDropdownMonthYearOpen,
    handleYearSelect,
  } = useCalendarPanel({ viewDate, minDate, maxDate, onSetViewDate })

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-neutral-100 bg-white">
      <div className="flex w-full items-center justify-between border-b border-neutral-100 p-4">
        <button
          type="button"
          onClick={onPrevMonth}
          disabled={isPrevDisabled}
          aria-label={t('calendar.prevMonth')}
          className="border-indigo-base flex size-10 items-center justify-center rounded-full border transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeftIcon className="text-indigo-base h-4.5 w-4.5" />
        </button>

        {dropdownMonthYear ? (
          <div className="flex items-center gap-2">
            <SelectField
              value={String(viewDate.getMonth())}
              options={monthOptions}
              variant="calendar"
              onSelect={(v) =>
                onSetViewDate?.(new Date(currentYear, Number(v), 1))
              }
              onOpenChange={(open) => setIsDropdownMonthYearOpen(open)}
            />
            <SelectField
              value={String(currentYear)}
              options={yearOptions}
              variant="calendar"
              onSelect={handleYearSelect}
              onOpenChange={(open) => setIsDropdownMonthYearOpen(open)}
            />
          </div>
        ) : (
          <Text className="text-neutral-900 capitalize">{monthYear}</Text>
        )}

        <button
          type="button"
          onClick={onNextMonth}
          disabled={isNextDisabled}
          aria-label={t('calendar.nextMonth')}
          className="border-indigo-base flex size-10 items-center justify-center rounded-full border transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronRightIcon className="text-indigo-base h-4.5 w-4.5" />
        </button>
      </div>

      <div
        className={`flex w-full flex-col gap-2 px-2 py-4 transition select-none ${isDropdownMonthYearOpen ? 'pointer-events-none opacity-50' : ''}`}
      >
        <div className="flex w-full">
          {weekdays.map((wd: string) => (
            <Span key={wd} className="text-indigo-base flex-1 text-center">
              {wd}
            </Span>
          ))}
        </div>

        <div
          className="flex w-full flex-col"
          onMouseLeave={() => onDayHover?.(undefined)}
        >
          {Array.from({ length: 6 }, (_, weekIdx) => (
            <div key={weekIdx} className="flex w-full">
              {days.slice(weekIdx * 7, weekIdx * 7 + 7).map((date) => {
                const isCurrentMonth = date.getMonth() === viewDate.getMonth()
                const isDisabled =
                  (!!minDate && isBeforeDay(date, minDate)) ||
                  (!!maxDate &&
                    !isSameDay(date, maxDate) &&
                    !isBeforeDay(date, maxDate))
                const isSingle = !!selectedDate && isSameDay(date, selectedDate)

                const effectiveEnd =
                  endDate ??
                  (hoverDate &&
                  startDate &&
                  !isBeforeDay(hoverDate, startDate) &&
                  !isSameDay(hoverDate, startDate)
                    ? hoverDate
                    : undefined)

                const rangeActive = !!startDate && !!effectiveEnd
                const isRangeStart = rangeActive && isSameDay(date, startDate!)
                const isRangeEnd = rangeActive && isSameDay(date, effectiveEnd!)
                const isBetween =
                  rangeActive &&
                  !isSameDay(date, startDate!) &&
                  !isSameDay(date, effectiveEnd!) &&
                  !isBeforeDay(date, startDate!) &&
                  isBeforeDay(date, effectiveEnd!)
                const isPartialStart =
                  !!startDate && !effectiveEnd && isSameDay(date, startDate)

                return (
                  <DayCell
                    key={date.getTime()}
                    date={date}
                    isCurrentMonth={isCurrentMonth}
                    isDisabled={isDisabled}
                    isSingleSelected={isSingle}
                    isRangeStart={isRangeStart}
                    isRangeEnd={isRangeEnd}
                    isBetween={isBetween}
                    isPartialStart={isPartialStart}
                    onClick={onDayClick}
                    onHover={onDayHover}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {(onCancel || onConfirm) && (
        <div className="flex items-center justify-end gap-4 border-t border-neutral-100 bg-white p-4">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onCancel}
            >
              {t('calendar.cancel')}
            </Button>
          )}
          {onConfirm && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onConfirm}
            >
              {t('calendar.confirm')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface DayCellProps {
  date: Date
  isCurrentMonth: boolean
  isDisabled: boolean
  isSingleSelected: boolean
  isRangeStart: boolean
  isRangeEnd: boolean
  isBetween: boolean
  isPartialStart: boolean
  onClick: (date: Date) => void
  onHover?: (date: Date | undefined) => void
}

function DayCell({
  date,
  isCurrentMonth,
  isDisabled,
  isSingleSelected,
  isRangeStart,
  isRangeEnd,
  isBetween,
  isPartialStart,
  onClick,
  onHover,
}: DayCellProps) {
  const day = date.getDate()

  if (!isCurrentMonth) {
    return (
      <div className="relative flex h-9 flex-1 items-center justify-center">
        <Span className="text-neutral-100">{day}</Span>
      </div>
    )
  }

  if (isDisabled) {
    return (
      <div className="relative flex h-9 flex-1 cursor-not-allowed items-center justify-center">
        <Span className="text-neutral-300">{day}</Span>
      </div>
    )
  }

  if (isRangeStart) {
    return (
      <button
        type="button"
        className="relative h-9 flex-1 outline-none"
        onClick={() => onClick(date)}
        onMouseEnter={() => onHover?.(date)}
      >
        <div className="absolute inset-y-0 right-0 w-1/2 bg-indigo-50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-indigo-base size-9 rounded-full" />
        </div>
        <Span className="absolute inset-0 z-10 flex items-center justify-center text-white">
          {day}
        </Span>
      </button>
    )
  }

  if (isRangeEnd) {
    return (
      <button
        type="button"
        className="relative h-9 flex-1 outline-none"
        onClick={() => onClick(date)}
        onMouseEnter={() => onHover?.(date)}
      >
        <div className="absolute inset-y-0 left-0 w-1/2 bg-indigo-50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-indigo-base size-9 rounded-full" />
        </div>
        <Span className="absolute inset-0 z-10 flex items-center justify-center text-white">
          {day}
        </Span>
      </button>
    )
  }

  if (isBetween) {
    return (
      <button
        type="button"
        className="relative h-9 flex-1 bg-indigo-50 outline-none"
        onClick={() => onClick(date)}
        onMouseEnter={() => onHover?.(date)}
      >
        <Span className="absolute inset-0 z-10 flex items-center justify-center text-neutral-900">
          {day}
        </Span>
      </button>
    )
  }

  if (isSingleSelected || isPartialStart) {
    return (
      <button
        type="button"
        className="relative h-9 flex-1 outline-none"
        onClick={() => onClick(date)}
        onMouseEnter={() => onHover?.(date)}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-indigo-base size-9 rounded-full" />
        </div>
        <Span className="absolute inset-0 z-10 flex items-center justify-center text-white">
          {day}
        </Span>
      </button>
    )
  }

  return (
    <button
      type="button"
      className="group relative h-9 flex-1 outline-none"
      onClick={() => onClick(date)}
      onMouseEnter={() => onHover?.(date)}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="size-9 rounded-full transition group-hover:bg-indigo-50" />
      </div>
      <Span className="absolute inset-0 z-10 flex items-center justify-center text-neutral-900">
        {day}
      </Span>
    </button>
  )
}
